import { C, OreKind } from '../game/constants';

export interface HudState {
  energy: number;
  energyMax: number;
  fuel: number;
  fuelMax: number;
  cargo: number;
  cargoMax: number;
  credits: number;
  depth: number;
  prompt: string;
  phase: string;
  interact?: boolean;   // context action available (e.g. dock in range)
}

export type UpgradeId = 'laser' | 'energy' | 'cargo' | 'thrust';

const UPGRADE_LABELS: Record<UpgradeId, string> = {
  laser: 'Laser Power',
  energy: 'Energy Cell',
  cargo: 'Cargo Hold',
  thrust: 'Thrusters',
};

/** DOM HUD: meters, prompt line, dock/sell/upgrade panel, toasts. */
export class Hud {
  private readonly root: HTMLElement;
  private energyFill!: HTMLElement;
  private cargoText!: HTMLElement;
  private creditsText!: HTMLElement;
  private depthText!: HTMLElement;
  private promptEl!: HTMLElement;
  private dockPanel!: HTMLElement;
  private toastEl!: HTMLElement;
  private toastTimer = 0;

  onSell: (() => void) | null = null;
  onLaunch: (() => void) | null = null;
  onBuy: ((id: UpgradeId) => void) | null = null;
  onPilot: (() => void) | null = null;
  onRefuel: (() => void) | null = null;
  onVirtualKey: ((code: string, down: boolean) => void) | null = null;
  onJoystick: ((x: number, y: number) => void) | null = null;

  constructor(parent: HTMLElement) {
    this.root = document.createElement('div');
    this.root.id = 'hud';
    this.root.innerHTML = `
      <div class="top">
        <div class="meter"><span class="label">ENERGY</span>
          <div class="bar"><div class="fill" id="energy-fill"></div></div></div>
        <div class="meter fuel"><span class="label">FUEL</span>
          <div class="bar"><div class="fill fuel-fill" id="fuel-fill"></div></div></div>
        <div class="stat"><span class="label">CARGO</span><span id="cargo-text">0/8</span></div>
        <div class="stat"><span class="label">CREDITS</span><span id="credits-text">0</span></div>
        <div class="stat"><span class="label">DEPTH</span><span id="depth-text">0m</span></div>
      </div>
      <div id="prompt"></div>
      <div id="toast" class="hidden"></div>
      <div id="dock-panel" class="hidden">
        <h2>MOTHERSHIP</h2>
        <button id="sell-btn">SELL CARGO</button>
        <div id="upgrade-rows"></div>
        <button id="refuel-btn">REFUEL</button>
        <button id="pilot-btn">PILOT MOTHERSHIP (M)</button>
        <button id="launch-btn">DROP TO PLANET</button>
      </div>
      <div id="joystick" class="hidden">
        <div id="joy-base"><div id="joy-knob"></div></div>
      </div>
      <div id="touch-left" class="touch-cluster hidden">
        <button class="tbtn" data-key="KeyA" id="t-left">◀</button>
        <button class="tbtn" data-key="KeyD" id="t-right">▶</button>
      </div>
      <div id="touch-right" class="touch-cluster right hidden">
        <button class="tbtn small" data-key="KeyB" id="t-bomb">💣</button>
        <button class="tbtn small" data-key="KeyM" id="t-helm">⎋</button>
        <button class="tbtn small" data-key="KeyS" id="t-retro">▼</button>
        <button class="tbtn big" data-key="KeyW" id="t-boost">▲</button>
        <button class="tbtn big" data-key="KeyE" id="t-interact">E</button>
        <button class="tbtn big" data-key="Space" id="t-fire">FIRE</button>
      </div>`;
    parent.appendChild(this.root);

    this.energyFill = this.root.querySelector('#energy-fill')!;
    this.cargoText = this.root.querySelector('#cargo-text')!;
    this.creditsText = this.root.querySelector('#credits-text')!;
    this.depthText = this.root.querySelector('#depth-text')!;
    this.promptEl = this.root.querySelector('#prompt')!;
    this.dockPanel = this.root.querySelector('#dock-panel')!;
    this.toastEl = this.root.querySelector('#toast')!;

    this.root.querySelector('#sell-btn')!.addEventListener('click', () => this.onSell?.());
    this.root.querySelector('#launch-btn')!.addEventListener('click', () => this.onLaunch?.());
    this.root.querySelector('#pilot-btn')!.addEventListener('click', () => this.onPilot?.());
    this.root.querySelector('#refuel-btn')!.addEventListener('click', () => this.onRefuel?.());

    // Joystick: drag the knob, normalized (-1..1, y up), snaps back on release
    {
      const area = this.root.querySelector<HTMLElement>('#joystick')!;
      const knob = this.root.querySelector<HTMLElement>('#joy-knob')!;
      const MAX = 52;
      let pid = -1;
      let cx = 0, cy = 0;
      const apply = (clientX: number, clientY: number) => {
        let dx = clientX - cx, dy = clientY - cy;
        const len = Math.hypot(dx, dy);
        if (len > MAX) { dx = (dx / len) * MAX; dy = (dy / len) * MAX; }
        knob.style.transform = `translate(${dx}px, ${dy}px)`;
        this.onJoystick?.(dx / MAX, -dy / MAX);   // screen y-down → game y-up
      };
      const release = () => {
        pid = -1;
        knob.style.transform = 'translate(0, 0)';
        this.onJoystick?.(0, 0);
      };
      area.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        pid = e.pointerId;
        const r = area.querySelector('#joy-base')!.getBoundingClientRect();
        cx = r.left + r.width / 2;
        cy = r.top + r.height / 2;
        try { area.setPointerCapture(pid); } catch { /* synthetic events */ }
        apply(e.clientX, e.clientY);
      });
      area.addEventListener('pointermove', (e) => {
        if (e.pointerId !== pid) return;
        e.preventDefault();
        apply(e.clientX, e.clientY);
      });
      area.addEventListener('pointerup', (e) => { if (e.pointerId === pid) release(); });
      area.addEventListener('pointercancel', (e) => { if (e.pointerId === pid) release(); });
    }

    // Touch buttons: press-and-hold semantics via pointer events
    this.root.querySelectorAll<HTMLElement>('.tbtn').forEach((b) => {
      const code = b.dataset.key!;
      const press = (e: Event) => {
        e.preventDefault();
        b.classList.add('held');
        this.onVirtualKey?.(code, true);
      };
      const release = (e: Event) => {
        e.preventDefault();
        b.classList.remove('held');
        this.onVirtualKey?.(code, false);
      };
      b.addEventListener('pointerdown', press);
      b.addEventListener('pointerup', release);
      b.addEventListener('pointercancel', release);
      b.addEventListener('pointerleave', release);
      b.addEventListener('contextmenu', (e) => e.preventDefault());
    });
  }

  update(s: HudState, dt: number): void {
    const pct = Math.max(0, (s.energy / s.energyMax) * 100);
    this.energyFill.style.width = `${pct}%`;
    this.energyFill.classList.toggle('low', pct < 25);
    const fuelFill = this.root.querySelector<HTMLElement>('#fuel-fill')!;
    const fpct = Math.max(0, (s.fuel / s.fuelMax) * 100);
    fuelFill.style.width = `${fpct}%`;
    fuelFill.classList.toggle('low', fpct < 22);
    this.cargoText.textContent = `${s.cargo}/${s.cargoMax}`;
    this.creditsText.textContent = `${s.credits}`;
    this.depthText.textContent = s.depth > 0 ? `${s.depth}m` : '—';
    this.promptEl.textContent = s.prompt;
    this.promptEl.classList.toggle('hidden', !s.prompt);

    if (this.toastTimer > 0) {
      this.toastTimer -= dt;
      if (this.toastTimer <= 0) this.toastEl.classList.add('hidden');
    }

    this.updateTouchLayout(s.phase, !!s.interact);
  }

  /** Contextual touch layout: joystick everywhere it makes sense, one-shot
   *  buttons on the right. The stick means: flight = lean + burn (push up),
   *  on foot = move + jetpack (push up), helm = direct thrust. */
  private updateTouchLayout(phase: string, interact: boolean): void {
    const show = (id: string, on: boolean) =>
      this.root.querySelector(`#${id}`)!.classList.toggle('hidden', !on);

    const idle = phase === 'DOCKED' || phase === 'DOCKING';
    const flying = phase === 'DESCENT' || phase === 'ASCENT';
    const helm = phase === 'MOTHERSHIP' || phase === 'SPACE';

    // Joystick is the PLAYER's controller (on foot). Ships fly on buttons:
    // ◀ ▶ rotate/burn direction, ▲ thrust/pitch-up, ▼ retro/pitch-down.
    show('joystick', phase === 'MINING');
    show('touch-left', flying || helm);
    show('touch-right', !idle);

    show('t-bomb', flying);
    show('t-boost', flying || helm || phase === 'SURFACE');
    show('t-retro', flying || helm);
    show('t-interact', phase === 'SURFACE' || phase === 'MINING' || interact);
    show('t-helm', phase === 'MOTHERSHIP');
    show('t-fire', phase === 'SPACE');
  }

  toast(msg: string, seconds = 2.2): void {
    this.toastEl.textContent = msg;
    this.toastEl.classList.remove('hidden');
    this.toastTimer = seconds;
  }

  showDock(
    cargoValue: number,
    levels: Record<UpgradeId, number>,
    credits: number,
  ): void {
    this.dockPanel.classList.remove('hidden');
    (this.root.querySelector('#sell-btn') as HTMLElement).textContent =
      `SELL CARGO (+${cargoValue})`;

    const rows = this.root.querySelector('#upgrade-rows')!;
    rows.innerHTML = '';
    (Object.keys(UPGRADE_LABELS) as UpgradeId[]).forEach((id) => {
      const level = levels[id];
      const cost = level >= 5 ? null : C.upgrades.costs[level - 1];
      const row = document.createElement('div');
      row.className = 'upgrade-row';
      row.innerHTML = `
        <span class="u-name">${UPGRADE_LABELS[id]}</span>
        <span class="u-pips">${'●'.repeat(level)}${'○'.repeat(5 - level)}</span>
        <button class="u-buy" ${cost === null || credits < cost ? 'disabled' : ''}>
          ${cost === null ? 'MAX' : cost}
        </button>`;
      row.querySelector('button')!.addEventListener('click', () => this.onBuy?.(id));
      rows.appendChild(row);
    });
  }

  hideDock(): void { this.dockPanel.classList.add('hidden'); }

  oreName(kind: OreKind): string {
    return kind.charAt(0).toUpperCase() + kind.slice(1);
  }
}
